import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import { usePermissions } from '../../context/PermissionContext';
import { isAdminRole } from '../AdminRoleGate';
import { ROUTES } from '../../constants/routes';
import { getCapabilities } from '../../services/api/hipApi';
import { LINK_COPY, SETTINGS_COPY } from '../../constants/abdmCopy';
import useAbdmModeStore, { hipConnectionOf } from '../../stores/abdmModeStore';

/**
 * HipRegistrationPanel — read-only header of the Send-to-ABDM page: "Health Facility on ABDM",
 * Connected / Not set up chip, and the HIP ID. Connected = HIP ID set and the status calls succeed.
 * `compact` = no card and no title: it sits inside the header ABDM menu, which supplies both.
 */
export default function HipRegistrationPanel({ onModeResolved, compact = false }) {
  const { isDark } = useTheme();
  const { role } = usePermissions();
  const [state, setState] = useState({ loading: true, error: '', reg: null });
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const store = useAbdmModeStore.getState();
        store.unblock();
        await Promise.all([getCapabilities(), store.fetchMode()]);
        const reg = useAbdmModeStore.getState().reg;
        if (!reg) throw Object.assign(new Error('registration status unavailable'), { userMessage: useAbdmModeStore.getState().error });
        if (cancelled) return;
        setState({ loading: false, error: '', reg });
        onModeResolved?.(reg.abdmMode);
      } catch (err) {
        if (cancelled) return;
        setState({ loading: false, error: err.userMessage || LINK_COPY.panelError, reg: null });
      }
    })();
    return () => { cancelled = true; };
  }, [onModeResolved]);

  const tryAgain = async () => {
    setChecking(true);
    await useAbdmModeStore.getState().refetch();
    setChecking(false);
    const { reg, error } = useAbdmModeStore.getState();
    if (reg) {
      setState({ loading: false, error: '', reg });
      onModeResolved?.(reg.abdmMode);
    } else {
      setState((s) => ({ ...s, error: error || s.error }));
    }
  };

  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const textMain = isDark ? '#f1f5f9' : '#111827';
  // Solid fill + named-shade border (not rgba < 0.1 alpha) so the card stays visible in dark mode (bk-2227).
  const cardStyle = compact ? {} : {
    background: isDark ? '#1e293b' : '#ffffff',
    border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
    borderRadius: 10, padding: 16, marginBottom: 20,
  };

  if (state.loading) {
    return <div style={{ ...cardStyle, color: textMuted, fontSize: 13 }}>{LINK_COPY.panelLoading}</div>;
  }
  if (state.error) {
    return (
      <div style={{ ...cardStyle, color: isDark ? '#f87171' : '#dc2626', fontSize: 13 }}>
        {state.error}{' '}
        <button
          type="button"
          onClick={tryAgain}
          disabled={checking}
          style={{
            marginLeft: 6, padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: checking ? 'default' : 'pointer', opacity: checking ? 0.6 : 1,
            background: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#93c5fd' : '#1d4ed8',
            border: `1px solid ${isDark ? '#475569' : '#93c5fd'}`, textDecoration: 'underline',
          }}
        >
          {checking ? LINK_COPY.panelChecking : LINK_COPY.panelTryAgain}
        </button>
      </div>
    );
  }

  const hipId = state.reg.abdmHipId;
  const { notEnabled, connected, pending } = hipConnectionOf(state.reg);
  const reasonLine = pending ? SETTINGS_COPY.statusReasons[state.reg.abdmHipStatusReason] : null;
  const regName = state.reg.abdmRegisteredName;
  const amber = { bg: isDark ? '#713f12' : '#fef9c3', color: isDark ? '#fef08a' : '#854d0e' };
  const chip = notEnabled
    ? { label: 'Not enabled for this organisation', bg: isDark ? '#713f12' : '#fef9c3', color: isDark ? '#fef08a' : '#854d0e' }
    : pending
    ? { label: SETTINGS_COPY.statusPending, ...amber }
    : connected
    ? { label: SETTINGS_COPY.statusConnected, bg: isDark ? '#14532d' : '#dcfce7', color: isDark ? '#bbf7d0' : '#166534' }
    : { label: 'Not set up', bg: isDark ? '#713f12' : '#fef9c3', color: isDark ? '#fef08a' : '#854d0e' };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!compact && <span style={{ fontSize: 14, fontWeight: 700, color: textMain }}>Health Facility on ABDM</span>}
        <span style={{
          display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 700,
          background: chip.bg, color: chip.color,
        }}>
          {chip.label}
        </span>
        {pending && isAdminRole(role) && (
          <Link to={ROUTES.SETTINGS} style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#93c5fd' : '#1d4ed8' }}>
            {SETTINGS_COPY.fixInSettings}
          </Link>
        )}
        {!connected && !notEnabled && isAdminRole(role) && (
          <Link to={ROUTES.SETTINGS} style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#93c5fd' : '#1d4ed8' }}>
            Add your HIP ID in Settings
          </Link>
        )}
      </div>
      {hipId && !notEnabled && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: textMuted, fontFamily: 'monospace' }}>
          {regName ? `${regName} (HIP ID: ${hipId})` : `HIP ID: ${hipId}`}
        </p>
      )}
      {reasonLine && <p style={{ margin: '4px 0 0', fontSize: 12, color: textMuted }}>{reasonLine}</p>}
    </div>
  );
}
